import React from 'react';
import { parlorScreen, useDesign } from '../../designs';
import DesignButton from '../../components/ui/DesignButton';

const d = parlorScreen.tokens;

interface OnboardingLayoutProps {
  step: 1 | 2 | 3;
  headline: string;
  children: React.ReactNode;
  buttonLabel: string;
  onSubmit: () => void;
  onBack?: () => void;
  onSkip?: () => void;
  isSubmitting?: boolean;
}

export default function OnboardingLayout({
  step,
  headline,
  children,
  buttonLabel,
  onSubmit,
  onBack,
  onSkip,
  isSubmitting,
}: OnboardingLayoutProps) {
  const { typography } = useDesign();
  const displayFont = `'${typography.displayFont}', serif`;
  const fs = typography.fontSize;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', minHeight: '100vh',
      backgroundColor: d.bgPrimary, padding: '32px 32px 0',
    }}>
      <div style={{
        flex: 1, maxWidth: '420px', width: '100%', margin: '0 auto',
        display: 'flex', flexDirection: 'column',
        paddingBottom: 'calc(160px + env(safe-area-inset-bottom, 0px))',
      }}>
        {/* KREWE wordmark */}
        <div style={{ textAlign: 'center', marginTop: '32px', marginBottom: '16px' }}>
          <span style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.wordmark, fontWeight: 800,
            letterSpacing: '9px', textTransform: 'uppercase',
            color: 'rgba(200,146,58,0.20)',
          }}>
            KREWE
          </span>
        </div>

        {/* Diamond progress dots */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div style={{
                width: '6px', height: '6px',
                backgroundColor: s <= step ? d.accent : 'transparent',
                border: s <= step ? 'none' : `1px solid ${d.textTertiary}`,
                transform: 'rotate(45deg)',
                transition: 'background-color 0.2s',
              }} />
              {s < 3 && (
                <div style={{
                  width: '16px', height: '1px',
                  backgroundColor: s < step ? 'rgba(200,146,58,0.25)' : 'rgba(90,78,112,0.30)',
                }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step label */}
        <p style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
          letterSpacing: '3px', textTransform: 'uppercase',
          color: 'rgba(200,146,58,0.40)', textAlign: 'center',
          margin: '0 0 24px',
        }}>
          STEP {step} OF 3
        </p>

        {/* Headline prompt */}
        <h2 style={{
          fontFamily: displayFont, fontSize: fs.screenHeader, fontWeight: 700,
          color: d.textPrimary, textAlign: 'center',
          margin: '0 0 24px', lineHeight: 1.2,
        }}>
          {headline}
        </h2>

        {/* Form content */}
        <div>
          {children}
        </div>
      </div>

      {/* Fixed bottom navigation bar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        backgroundColor: d.bgPrimary,
        borderTop: `1px solid ${d.border}`,
        padding: '16px 32px calc(24px + env(safe-area-inset-bottom, 0px))',
        zIndex: 50,
      }}>
        <div style={{ maxWidth: '420px', margin: '0 auto' }}>
          {step === 3 ? (
            <DesignButton tier={2} fullWidth onClick={onSubmit} disabled={isSubmitting}>
              {buttonLabel}
            </DesignButton>
          ) : (
            <button onClick={onSubmit} disabled={isSubmitting} type="button" style={{
              display: 'block', width: '100%', padding: '11px 20px',
              background: 'none', border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
              letterSpacing: '2.5px', textTransform: 'uppercase',
              color: d.accent, textAlign: 'center',
              opacity: isSubmitting ? 0.5 : 1,
            }}>
              {buttonLabel} →
            </button>
          )}

          {onSkip && (
            <button onClick={onSkip} disabled={isSubmitting} type="button" style={{
              display: 'block', width: '100%', marginTop: '8px', padding: '6px',
              background: 'none', border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
              color: d.textSecondary, textAlign: 'center',
              opacity: isSubmitting ? 0.5 : 1,
            }}>
              Skip for now
            </button>
          )}

          {onBack && (
            <button onClick={onBack} type="button" style={{
              display: 'block', width: '100%', marginTop: '4px', padding: '6px',
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 300,
              color: d.textTertiary, textAlign: 'center',
            }}>
              Back
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
